/**
 * WsReconnectCoordinator
 *
 * Prevents the "reconnect storm" that causes the page to freeze after a tab
 * has been hidden for a long time.
 *
 * Background:
 *   y-websocket's internal reconnect loop resets its exponential backoff on
 *   every successful WebSocket.onopen event — even if the server immediately
 *   closes the handshake (stale session, rate-limited room, etc.). When a tab
 *   is hidden, the browser aggressively throttles timers so reconnect attempts
 *   silently queue up. When the tab becomes visible again, every queued retry
 *   fires near-simultaneously, hammering the backend at ~100ms intervals and
 *   flooding the console with WebSocket errors — often locking the main thread.
 *
 * Strategy:
 *   1. Hidden > HIDDEN_PAUSE_THRESHOLD_MS → call provider.disconnect() on every
 *      registered provider that is currently connected or connecting. This
 *      tears down y-websocket's internal retry timer, guaranteeing no queued
 *      reconnects can accumulate.
 *   2. Visible again (after we paused) → after a short settle delay, call
 *      provider.connect() one at a time, staggered by RECONNECT_STAGGER_MS,
 *      so the backend never sees N simultaneous opens from a single client.
 *   3. Providers disconnected by their owner (not by us) are never reconnected
 *      — we track our own pauses in a WeakSet.
 *
 * Registration is piggy-backed onto wsTokenRefresher.register/unregister so
 * the common case requires zero call-site changes.
 */
import type { WebsocketProvider } from 'y-websocket';
import { createFileLogger } from '../utils/logger';
import { isHardStopped } from './yjs/wsHardStop';

const log = createFileLogger('WS_RECONNECT_COORD', { enabled: true });

// Pause providers once the tab has been hidden for this long. Short hides
// (tab switches, app switches) stay well under this threshold so normal
// backoff handles them — we only intervene when the backoff loop has had
// enough time to go pathological in a throttled background tab.
const HIDDEN_PAUSE_THRESHOLD_MS = 30_000;

// After the tab becomes visible, wait this long before starting the
// staggered resume. Gives the event loop a moment to breathe after the
// browser un-throttles and prevents racing with any in-flight teardown.
const VISIBILITY_SETTLE_MS = 500;

// Gap between each provider's resume. Prevents thundering-herd opens
// against the WS server when many rooms are active at once.
const RECONNECT_STAGGER_MS = 300;

// Connection-health watchdog cadence. While the tab is visible we periodically
// sweep for providers stranded in a disconnected state and re-issue connect().
// y-websocket only auto-reconnects while its internal `shouldConnect` flag is
// true; a socket left disconnected by a pause that never resumed, an HMR orphan,
// or a partial teardown would otherwise NEVER come back on its own. This sweep
// is the catch-all that guarantees recovery regardless of what caused the drop.
const HEALTH_CHECK_INTERVAL_MS = 10_000;

class WsReconnectCoordinator {
  private readonly providers = new Set<WebsocketProvider>();
  private readonly pausedByUs = new WeakSet<WebsocketProvider>();
  // Per-provider predicates that, when truthy at pause time, keep that provider
  // connected through a long hide. Used by conversation rooms with an in-flight
  // agent run: tearing the socket down mid-run drops live streaming events
  // (the server keeps pushing into a room with clients=0), so the run only
  // becomes visible again on a full reload.
  private readonly exemptionChecks = new Map<WebsocketProvider, () => boolean>();
  private hiddenSince: number | null = null;
  private hiddenTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly resumeTimers = new Set<ReturnType<typeof setTimeout>>();
  // Separate from resumeTimers so the visibility-resume scheduling guard
  // (`resumeTimers.size === 0`) is never confused by an in-flight health sweep.
  private readonly healthTimers = new Set<ReturnType<typeof setTimeout>>();
  private readonly longHideHandlers = new Set<() => void>();

  constructor() {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => this.onVisibilityChange());
    }
    if (typeof window !== 'undefined') {
      // Safari/Firefox bfcache restore: visibilitychange may not fire reliably,
      // so pageshow.persisted is the canonical signal to treat as "visible again".
      window.addEventListener('pageshow', (e) => {
        if ((e as PageTransitionEvent).persisted) {
          this.handleVisible();
        }
      });
      // Network restored — short-circuit y-websocket's exponential backoff and
      // reconnect any stranded provider immediately instead of waiting for the
      // next backoff tick (up to 30s) or the next watchdog sweep.
      window.addEventListener('online', () => this.reconnectStrandedProviders());
    }
    // Periodic connection-health watchdog. Created once at module load under
    // real timers; the body is also exposed publicly so tests can drive it
    // deterministically without depending on this interval firing.
    if (typeof setInterval !== 'undefined') {
      const handle = setInterval(() => this.reconnectStrandedProviders(), HEALTH_CHECK_INTERVAL_MS);
      // Never keep a Node process (tests / SSR) alive on account of the watchdog.
      (handle as unknown as { unref?: () => void })?.unref?.();
    }
  }

  register(provider: WebsocketProvider): void {
    this.providers.add(provider);
  }

  unregister(provider: WebsocketProvider): void {
    this.providers.delete(provider);
    this.pausedByUs.delete(provider);
    this.exemptionChecks.delete(provider);
  }

  /**
   * Register a predicate that exempts a provider from the long-hide pause while
   * it returns true. Re-evaluated on every pause, so a provider is only kept
   * alive for as long as the condition holds (e.g. an active agent run). Pass
   * the same provider again to replace the check; call clearPauseExemption to
   * remove it.
   */
  setPauseExemption(provider: WebsocketProvider, check: () => boolean): void {
    this.exemptionChecks.set(provider, check);
  }

  clearPauseExemption(provider: WebsocketProvider): void {
    this.exemptionChecks.delete(provider);
  }

  /**
   * Register a handler to call when the tab has been hidden long enough to trigger
   * a provider pause (i.e. when HIDDEN_PAUSE_THRESHOLD_MS is exceeded). Use this
   * to tear down expensive ephemeral resources (e.g. conversation Y.Docs) that
   * should not keep their in-memory state across a long background interval.
   * Returns an unsubscribe function.
   */
  onLongHide(handler: () => void): () => void {
    this.longHideHandlers.add(handler);
    return () => this.longHideHandlers.delete(handler);
  }

  private onVisibilityChange(): void {
    if (typeof document === 'undefined') return;
    if (document.visibilityState === 'hidden') {
      this.handleHidden();
    } else {
      this.handleVisible();
    }
  }

  private handleHidden(): void {
    if (this.hiddenTimer !== null) return;
    // Cancel any in-flight resume/health reconnect — we're hidden again, so
    // completing a staggered reconnect would open WS connections in a
    // now-throttled tab (the storm pauseAll() exists to prevent).
    this.clearResumeTimers();
    this.clearHealthTimers();
    this.hiddenSince = Date.now();
    this.hiddenTimer = setTimeout(() => {
      this.hiddenTimer = null;
      this.pauseAll();
    }, HIDDEN_PAUSE_THRESHOLD_MS);
  }

  private handleVisible(): void {
    if (this.hiddenTimer !== null) {
      clearTimeout(this.hiddenTimer);
      this.hiddenTimer = null;
    }
    const hiddenForMs = this.hiddenSince !== null ? Date.now() - this.hiddenSince : 0;
    this.hiddenSince = null;

    // Only schedule a resume if we actually paused AND no resume is already
    // in flight. Without the second guard, a spurious duplicate 'visible'
    // event would call clearResumeTimers() and cancel an in-flight resume
    // without scheduling a replacement — leaving providers disconnected.
    if (hiddenForMs >= HIDDEN_PAUSE_THRESHOLD_MS && this.resumeTimers.size === 0) {
      const settle = setTimeout(() => {
        this.resumeTimers.delete(settle);
        this.resumeAllStaggered();
      }, VISIBILITY_SETTLE_MS);
      this.resumeTimers.add(settle);
    }
  }

  private pauseAll(): void {
    let paused = 0;
    let exempt = 0;
    // Snapshot the set — a misbehaving provider's disconnect() could
    // synchronously trigger an unregister() via status listeners, and
    // iterating a mutating Set can skip entries.
    for (const provider of Array.from(this.providers)) {
      try {
        const connected = Boolean((provider as { wsconnected?: boolean }).wsconnected);
        const connecting = Boolean((provider as { wsconnecting?: boolean }).wsconnecting);
        if (!connected && !connecting) continue;
        // Keep exempt providers (e.g. conversation rooms with an in-flight run)
        // connected so live streaming events aren't lost to a clients=0 room.
        const check = this.exemptionChecks.get(provider);
        if (check) {
          let isExempt = false;
          try { isExempt = check(); } catch (err) { log.warn('Exemption check threw:', err); }
          if (isExempt) { exempt += 1; continue; }
        }
        provider.disconnect();
        this.pausedByUs.add(provider);
        paused += 1;
      } catch (err) {
        log.warn('Error pausing provider:', err);
      }
    }
    if (paused > 0 || exempt > 0) {
      log.log(`Tab hidden >${HIDDEN_PAUSE_THRESHOLD_MS}ms — paused ${paused} WS provider(s)${exempt > 0 ? `, kept ${exempt} exempt (active run)` : ''}`);
    }

    // Notify long-hide handlers so callers can tear down ephemeral resources
    // (e.g. conversation Y.Docs) whose bloated in-memory state would cause
    // expensive resync payloads on the next reconnect.
    for (const handler of this.longHideHandlers) {
      try { handler(); } catch (err) {
        log.warn('Error in long-hide handler:', err);
      }
    }
  }

  private resumeAllStaggered(): void {
    const toResume: WebsocketProvider[] = [];
    for (const provider of this.providers) {
      if (this.pausedByUs.has(provider)) {
        toResume.push(provider);
      }
    }
    if (toResume.length === 0) return;

    log.log(
      `Tab visible — resuming ${toResume.length} paused WS provider(s) (stagger=${RECONNECT_STAGGER_MS}ms)`
    );

    toResume.forEach((provider, idx) => {
      const timer = setTimeout(() => {
        this.resumeTimers.delete(timer);
        if (!this.providers.has(provider)) return;
        if (!this.pausedByUs.has(provider)) return;
        try {
          provider.connect();
          this.pausedByUs.delete(provider);
        } catch (err) {
          log.warn('Error resuming provider:', err);
        }
      }, idx * RECONNECT_STAGGER_MS);
      this.resumeTimers.add(timer);
    });
  }

  private clearResumeTimers(): void {
    for (const t of this.resumeTimers) clearTimeout(t);
    this.resumeTimers.clear();
  }

  private clearHealthTimers(): void {
    for (const t of this.healthTimers) clearTimeout(t);
    this.healthTimers.clear();
  }

  private isLive(provider: WebsocketProvider): boolean {
    const connected = Boolean((provider as { wsconnected?: boolean }).wsconnected);
    const connecting = Boolean((provider as { wsconnecting?: boolean }).wsconnecting);
    return connected || connecting;
  }

  /**
   * Returns true only when the provider itself wants to be connected.
   *
   * y-websocket maintains a `shouldConnect` flag that is:
   *   - `true` on construction and after every `connect()` call
   *   - `false` after every `disconnect()` call
   *
   * Involuntary drops (network blip, server close) only set `wsconnected=false`
   * via `closeWebsocketConnection` — they NEVER touch `shouldConnect`. So
   * `shouldConnect === true` is exactly "y-websocket wants to be connected but
   * currently isn't" (the only case where forcing `connect()` is correct).
   *
   * Any deliberate teardown — our own `pauseAll()`, hard-stop, collab disconnect
   * button, corruption handler — goes through `disconnect()` which sets
   * `shouldConnect=false`. Gating on this flag means we never fight intentional
   * states regardless of which code path caused the disconnect.
   */
  private wantsConnection(provider: WebsocketProvider): boolean {
    return Boolean((provider as { shouldConnect?: boolean }).shouldConnect);
  }

  /**
   * Connection-health watchdog body — the universal "always reconnect" guarantee.
   *
   * Re-issues `connect()` on any registered provider that is disconnected AND
   * still wants to be connected (`shouldConnect === true`). When a provider is
   * genuinely stranded — e.g. y-websocket's backoff is stuck, the network was
   * briefly lost, or an HMR orphan slipped past the globalThis guards — this
   * sweep re-arms `setupWS` within at most one watchdog interval.
   *
   * Driven by the periodic interval AND the `window 'online'` event. Public so
   * tests can invoke it deterministically.
   *
   * Guards (each prevents fighting an intentional state):
   *   - Tab must be visible and not pending-pause — never reconnect in a
   *     throttled background tab, and never undo the deliberate hidden-pause.
   *   - `wantsConnection(provider)` — the definitive gate: any `disconnect()`
   *     (our pause, hard-stop, collab disconnect, corruption handler) sets
   *     `shouldConnect=false`; we must not override that.
   *   - `pausedByUs` is kept as an explicit, fast-path skip so it is visible
   *     in the logic even though it implies `shouldConnect=false`.
   *   - `isHardStopped` is kept for the same reason, and because hard-stop
   *     also shadows `connect()` on the provider instance, so calling it would
   *     spam the gated-connect warning every tick.
   *   - Reconnects are staggered to avoid a thundering herd after a mass outage.
   */
  reconnectStrandedProviders(): void {
    if (this.hiddenSince !== null || this.hiddenTimer !== null) return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;

    const stranded: WebsocketProvider[] = [];
    for (const provider of Array.from(this.providers)) {
      if (this.pausedByUs.has(provider)) continue;
      if (!this.wantsConnection(provider)) continue;
      try {
        if (isHardStopped(provider)) continue;
      } catch (err) {
        log.warn('isHardStopped check threw — skipping provider:', err);
        continue;
      }
      if (this.isLive(provider)) continue;
      stranded.push(provider);
    }
    if (stranded.length === 0) return;

    log.log(
      `Health check: reconnecting ${stranded.length} stranded WS provider(s) (stagger=${RECONNECT_STAGGER_MS}ms)`
    );

    stranded.forEach((provider, idx) => {
      const timer = setTimeout(() => {
        this.healthTimers.delete(timer);
        // Re-validate at fire time — state may have changed during the stagger.
        if (!this.providers.has(provider)) return;
        if (this.pausedByUs.has(provider)) return;
        if (!this.wantsConnection(provider)) return;
        if (this.isLive(provider)) return;
        try {
          if (isHardStopped(provider)) return;
          provider.connect();
        } catch (err) {
          log.warn('Error reconnecting stranded provider:', err);
        }
      }, idx * RECONNECT_STAGGER_MS);
      this.healthTimers.add(timer);
    });
  }

  /** Debug helper — snapshot of current coordinator state. */
  public getDebugState(): {
    registeredCount: number;
    exemptionCount: number;
    hiddenSince: number | null;
    hiddenForMs: number;
    pendingPause: boolean;
    pendingResumes: number;
    pendingHealthReconnects: number;
  } {
    return {
      registeredCount: this.providers.size,
      exemptionCount: this.exemptionChecks.size,
      hiddenSince: this.hiddenSince,
      hiddenForMs: this.hiddenSince !== null ? Date.now() - this.hiddenSince : 0,
      pendingPause: this.hiddenTimer !== null,
      pendingResumes: this.resumeTimers.size,
      pendingHealthReconnects: this.healthTimers.size,
    };
  }
}

// Preserve the singleton across Vite HMR module re-executions (same pattern as
// wsClientSingleton and SystemContext). Without this, editing any file in the
// coordinator's import chain re-runs the constructor, adding duplicate event
// listeners and a second watchdog interval — while the preserved wsClient still
// holds providers registered to the old coordinator instance that the new one
// can no longer see (split-brain). One stable instance for the lifetime of the
// page is the correct invariant.
const _COORD_KEY = Symbol.for('__river_ws_reconnect_coordinator__');
const _coordGlobal = globalThis as unknown as { [_COORD_KEY]?: WsReconnectCoordinator };
export const wsReconnectCoordinator: WsReconnectCoordinator =
  _coordGlobal[_COORD_KEY] ?? (_coordGlobal[_COORD_KEY] = new WsReconnectCoordinator());
